import { createApi } from '@reduxjs/toolkit/query/react'
import { hcPortalBaseQuery } from '@/features/baseQuery.ts'
import {
  AddRewardExerciseConfigRequest,
  AddRewardExerciseRoundRequest,
  BulkUpdateRewardExercisesRequest,
  DownloadRewardExerciseInitTemplateRequest,
  DownloadRewardStatementTemplateRequest,
  GetRewardExerciseDetailsRequest,
  ImportRewardExerciseInitTemplateRequest,
  LockRewardExerciseRoundRequest,
  RemoveRewardExerciseRoundRequest,
  RemoveRewardExercisesRequest,
  SyncRewardExerciseDataRequest,
  UnlockRewardExerciseRoundRequest,
  UpdateRewardExerciseConfigRequest,
  UpdateRewardExerciseDepartmentConfigRequest,
  UpdateRewardExerciseRequest,
} from '@/features/reward-exercise/reward-exercise-request.def.ts'
import { HcError } from '@/common/types/api.def.ts'
import { downloadExcel, downloadFile } from '@/utils/misc.ts'
import { RewardExerciseDetails, RewardExerciseItem } from '@/features/reward-exercise/reward-exercise.def.ts'

export const rewardExerciseApi = createApi({
  reducerPath: 'rewardExercise',
  baseQuery: hcPortalBaseQuery(),
  tagTypes: ['RewardExercise'],
  endpoints: (builder) => ({
    downloadRewardExerciseInitTemplate: builder.mutation<void, DownloadRewardExerciseInitTemplateRequest>({
      query: (request) => ({
        url: '/RewardExercise/DownloadRewardExerciseInitTemplate',
        method: 'POST',
        cache: 'no-cache',
        body: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadExcel(`reward_exercise_template_${request.year}`, blob)
        },
      }),
    }),
    downloadRewardStatementTemplate: builder.mutation<void, DownloadRewardStatementTemplateRequest>({
      query: (request) => ({
        url: '/RewardExercise/DownloadRewardStatementTemplate',
        method: 'POST',
        cache: 'no-cache',
        body: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadExcel(`reward_statement_template_${request.year}`, blob)
        },
      }),
    }),
    downloadInvestmentTeamRewardExerciseTemplate: builder.mutation<void, DownloadRewardExerciseInitTemplateRequest>({
      query: (request) => ({
        url: '/RewardExercise/DownloadInvestmentTeamRewardExerciseTemplate',
        method: 'POST',
        cache: 'no-cache',
        body: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadExcel(`reward_exercise_investment_template_${request.year}`, blob)
        },
      }),
    }),
    importRewardExerciseInitTemplate: builder.mutation<void, ImportRewardExerciseInitTemplateRequest>({
      query: (request) => {
        const formData = new FormData()
        formData.append('year', request.year.toString())
        formData.append('excelFile', request.excelFile)

        return {
          url: '/RewardExercise/ImportRewardExerciseInitTemplate',
          method: 'POST',
          cache: 'no-cache',
          body: formData,
        }
      },
      invalidatesTags: (_result, _error, arg) => [{ type: 'RewardExercise', id: arg.year }],
    }),
    getRewardExerciseDetails: builder.query<RewardExerciseDetails, GetRewardExerciseDetailsRequest>({
      query: (request) => ({
        url: '/RewardExercise/GetRewardExerciseDetails',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'RewardExercise', id: arg.year }],
    }),
    downloadRewardExerciseDetails: builder.mutation<void, GetRewardExerciseDetailsRequest>({
      query: (request) => ({
        url: '/RewardExercise/DownloadRewardExerciseDetails',
        method: 'GET',
        cache: 'no-cache',
        params: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadFile('reward_exercise.xlsx', blob)
        },
      }),
    }),
    updateRewardExercise: builder.mutation<RewardExerciseItem, UpdateRewardExerciseRequest>({
      query: (request) => ({
        url: '/RewardExercise/UpdateRewardExercise',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      onQueryStarted: async (request, { dispatch, queryFulfilled }) => {
        const { data: updated } = await queryFulfilled
        dispatch(rewardExerciseApi.util.updateQueryData(
          'getRewardExerciseDetails',
          { year: updated.year, fxRefDate: request.fxRefDate } as GetRewardExerciseDetailsRequest,
          draft => ({ ...draft, data: draft.data.map(item => item.id === updated.id ? updated : item) }),
        ))
      },
    }),
    bulkUpdateRewardExercises: builder.mutation<RewardExerciseItem[], BulkUpdateRewardExercisesRequest>({
      query: (request) => ({
        url: '/RewardExercise/BulkUpdateRewardExercises',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      onQueryStarted: async (request, { dispatch, queryFulfilled }) => {
        const { data: updated } = await queryFulfilled
        dispatch(rewardExerciseApi.util.updateQueryData(
          'getRewardExerciseDetails',
          { year: request.year, fxRefDate: request.fxRefDate } as GetRewardExerciseDetailsRequest,
          draft => {
            const updatedDict = updated.reduce((acc, item) => ({
              ...acc,
              [item.id]: item,
            }), {} as Record<number, RewardExerciseItem>)
            return { ...draft, data: draft.data.map(item => updatedDict[item.id] ? updatedDict[item.id] : item) }
          },
        ))
      },
    }),
    removeRewardExercises: builder.mutation<boolean, RemoveRewardExercisesRequest>({
      query: (request) => ({
        url: '/RewardExercise/RemoveRewardExercises',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      onQueryStarted: async (request, { dispatch }) => {
        dispatch(rewardExerciseApi.util.updateQueryData(
          'getRewardExerciseDetails',
          { year: request.year, fxRefDate: request.fxRefDate } as GetRewardExerciseDetailsRequest,
          draft => ({ ...draft, data: draft.data.filter(item => !request.ids.includes(item.id)) }),
        ))
      },
    }),
    addRewardExerciseRound: builder.mutation<boolean, AddRewardExerciseRoundRequest>({
      query: (request) => ({
        url: '/RewardExercise/AddRewardExerciseRound',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'RewardExercise', id: arg.year }],
    }),
    removeRewardExerciseRound: builder.mutation<boolean, RemoveRewardExerciseRoundRequest>({
      query: (request) => ({
        url: '/RewardExercise/RemoveRewardExerciseRound',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'RewardExercise', id: arg.year }],
    }),
    bulkLockRewardExerciseRound: builder.mutation<boolean, LockRewardExerciseRoundRequest>({
      query: (request) => ({
        url: '/RewardExercise/BulkLockRewardExerciseRound',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'RewardExercise', id: arg.year }],
    }),
    unlockRewardExerciseRound: builder.mutation<boolean, UnlockRewardExerciseRoundRequest>({
      query: (request) => ({
        url: '/RewardExercise/UnlockRewardExerciseRound',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      onQueryStarted: async (request, { dispatch }) => {
        dispatch(rewardExerciseApi.util.updateQueryData(
          'getRewardExerciseDetails',
          { year: request.year, fxRefDate: request.fxRefDate } as GetRewardExerciseDetailsRequest,
          draft => ({
            ...draft, data: draft.data.map(item => item.id === request.id ? {
              ...item,
              proposedRewards: item.proposedRewards.map((round, index) => index === request.index ? {
                ...round,
                disabledAt: null,
                disabled: false,
              } : round),
            } as RewardExerciseItem : item),
          }),
        ))
      },
    }),
    addRewardExerciseConfig: builder.mutation<void, AddRewardExerciseConfigRequest>({
      query: (request) => ({
        url: '/RewardExercise/AddRewardExerciseConfig',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'RewardExercise', id: arg.year }],
    }),
    updateRewardExerciseConfig: builder.mutation<boolean, UpdateRewardExerciseConfigRequest>({
      query: (request) => ({
        url: '/RewardExercise/UpdateRewardExerciseConfig',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'RewardExercise', id: arg.year }],
    }),
    updateRewardExerciseDepartmentConfig: builder.mutation<boolean, UpdateRewardExerciseDepartmentConfigRequest>({
      query: (request) => ({
        url: '/RewardExercise/UpdateRewardExerciseDepartmentConfig',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'RewardExercise', id: arg.year }],
    }),
    syncRewardExerciseData: builder.mutation<boolean, SyncRewardExerciseDataRequest>({
      query: (request) => ({
        url: '/RewardExercise/SyncRewardExerciseData',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'RewardExercise', id: arg.year }],
    }),
  }),
})

export const {
  useDownloadRewardExerciseInitTemplateMutation,
  useDownloadInvestmentTeamRewardExerciseTemplateMutation,
  useImportRewardExerciseInitTemplateMutation,
  useGetRewardExerciseDetailsQuery,
  useDownloadRewardExerciseDetailsMutation,
  useUpdateRewardExerciseMutation,
  useAddRewardExerciseConfigMutation,
  useBulkUpdateRewardExercisesMutation,
  useRemoveRewardExercisesMutation,
  useAddRewardExerciseRoundMutation,
  useRemoveRewardExerciseRoundMutation,
  useBulkLockRewardExerciseRoundMutation,
  useUnlockRewardExerciseRoundMutation,
  useUpdateRewardExerciseConfigMutation,
  useUpdateRewardExerciseDepartmentConfigMutation,
  useSyncRewardExerciseDataMutation,
  useDownloadRewardStatementTemplateMutation,
} = rewardExerciseApi